import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { HUD } from "./theme";

const COLORS = ["#22d3ee", "#f59e0b", "#f43f5e", "#a3e635", "#e879f9", "#60a5fa", "#fb923c", "#34d399"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n) => String(n).padStart(2, "0");

// Which scheduled look owns a given day (same rule as the server: one-time ranges or yearly MM-DD windows)
const lookOn = (looks, year, ymd) => looks.find((l) => {
  if (l.yearly) {
    const [s, e, t] = [l.start.slice(5), l.end.slice(5), ymd.slice(5)];
    return s <= e ? s <= t && t <= e : t >= s || t <= e;
  }
  return l.start <= ymd && ymd <= l.end;
});

const Month = ({ year, m, looks, color }) => {
  const first = new Date(year, m, 1).getDay();
  const days = new Date(year, m + 1, 0).getDate();
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Phoenix" });
  return (
    <div className="rounded-lg border border-white/5 p-2" data-testid={`look-cal-month-${m}`}>
      <p className="text-[11px] text-gray-300 mb-1">{MONTHS[m]}</p>
      <div className="grid grid-cols-7 gap-[2px]">
        {[...Array(first)].map((_, i) => <span key={`b${i}`} />)}
        {[...Array(days)].map((_, i) => {
          const ymd = `${year}-${pad(m + 1)}-${pad(i + 1)}`;
          const l = lookOn(looks, year, ymd);
          return <span key={i} title={l ? `${l.name} — ${ymd}` : ymd} className={`h-5 text-[10px] leading-5 text-center rounded-sm ${ymd === today ? "ring-1 ring-white" : ""} ${l ? "text-black font-semibold" : "text-gray-400"}`} style={l ? { background: color(l) } : undefined} data-testid={l ? `look-cal-day-${ymd}` : undefined}>{i + 1}</span>;
        })}
      </div>
    </div>
  );
};

export const LookCalendar = ({ looks }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const sched = looks.filter((l) => l.start && l.end).sort((a, b) => a.start.localeCompare(b.start));
  const color = (l) => COLORS[sched.indexOf(l) % COLORS.length];
  return (
    <div className="space-y-3 rounded-xl border border-cyan-400/15 p-3" data-testid="look-calendar">
      <div className="flex items-center gap-2">
        <button onClick={() => setYear(year - 1)} className={`${HUD.ghost} h-7 w-7 flex items-center justify-center`} data-testid="look-cal-prev"><ChevronLeft className="w-4 h-4" /></button>
        <p className="text-sm text-white w-12 text-center" data-testid="look-cal-year">{year}</p>
        <button onClick={() => setYear(year + 1)} className={`${HUD.ghost} h-7 w-7 flex items-center justify-center`} data-testid="look-cal-next"><ChevronRight className="w-4 h-4" /></button>
        <div className="flex flex-wrap gap-3 ml-3">
          {sched.length === 0 ? <span className="text-[11px] text-gray-500" data-testid="look-cal-empty">No looks scheduled yet — use the calendar icon on a look to add dates.</span>
            : sched.map((l) => <span key={l.id} className="flex items-center gap-1.5 text-[11px] text-gray-300"><span className="w-3 h-3 rounded-sm" style={{ background: color(l) }} />{l.name}</span>)}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {MONTHS.map((_, m) => <Month key={m} year={year} m={m} looks={sched} color={color} />)}
      </div>
    </div>
  );
};
